import { useEffect, useState } from "react";

import { baaChunkLoader, loadBaaComponent } from "./index";

// Every session that has a BAA at all can render the platform's own.
export const FALLBACK_BAA_VARIANT = "lucas";

// Resolve a BAA variant's body component from its own chunk.
//
// Deliberately NOT React.lazy: two callers render the agreement offscreen and
// then read the live DOM to build a PDF. Suspense would hand them an empty
// node on the first commit, silently exporting a blank agreement. Exposing
// `ready` lets those callers wait for the real body to be mounted.
//
// Unknown variants are normalized HERE rather than at each call site. A
// caller that passed one through un-normalized got `ready: false` forever —
// no component, no error — which showed as a permanent spinner and, on the
// export paths, a document that never generated. Normalizing at the single
// choke point means every consumer of this hook inherits the same behavior.
//
// `variant` may be null to skip loading entirely (`ready: false`, no error).
// A chunk that fails to load resolves to `error` so callers can fail loudly
// instead of waiting on a `ready` that will never arrive.
export function useBaaComponent(variant) {
  const resolved = variant ? (baaChunkLoader(variant) ? variant : FALLBACK_BAA_VARIANT) : null;
  const [state, setState] = useState({ Component: null, ready: false, error: null });

  useEffect(() => {
    let alive = true;
    if (!resolved) {
      setState({ Component: null, ready: false, error: null });
      return undefined;
    }
    setState({ Component: null, ready: false, error: null });
    loadBaaComponent(resolved).then((Component) => {
      if (!alive) return;
      if (Component) setState({ Component, ready: true, error: null });
      else setState({ Component: null, ready: false, error: new Error(`BAA body unavailable: ${resolved}`) });
    });
    return () => {
      alive = false;
    };
  }, [resolved]);

  return state;
}

export default useBaaComponent;
